/**
 * A deterministic reader for assignment prompts.
 *
 * It looks for five kinds of thing, each with patterns rather than understanding:
 * directive (task) words, length limits, source requirements, a named citation style,
 * dates, and formatting rules — then picks out the content terms that are left. Every
 * finding carries the character span it came from, so the widget can highlight the
 * reader's own prompt rather than restate it.
 *
 * The patterns are deliberately conservative. A past participle ("as discussed in
 * class", "will be evaluated on") is not a task, so verbs match in their base, -s and
 * -ing forms only; acronyms that double as organization names (ASA, AMA, ACS, CSE)
 * count as a citation style only beside a word like "style" or an edition number; and a
 * slash date ("1/3") counts only with a year or a deadline word nearby, because "worth
 * 1/3 of your grade" is not a due date.
 */

export interface Span {
  start: number;
  end: number;
}

/* ----------------------------------------------------------------- numbers */

const UNIT_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const TENS_WORDS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const NUMBER_WORD = `(?:(?:${Object.keys(TENS_WORDS).join("|")})(?:-(?:${Object.keys(
  UNIT_WORDS
)
  .slice(0, 9)
  .join(
    "|"
  )}))?|${Object.keys(UNIT_WORDS).join("|")}|a\\s+hundred|one\\s+hundred)`;

/** A count as people write one: "1,500", "1500", "2.5", "five", "twenty-five". */
const NUM = `(?:\\d{1,3}(?:,\\d{3})+|\\d+(?:\\.\\d+)?|${NUMBER_WORD})`;

const RE_THOUSANDS = /,/g;
const RE_HYPHEN_OR_SPACE = /[-\s]+/;

/** Read a count matched by `NUM`. */
export function parseCount(raw: string): number | null {
  const text = raw.trim().toLowerCase();
  const digits = Number(text.replace(RE_THOUSANDS, ""));
  if (text !== "" && Number.isFinite(digits)) {
    return digits;
  }
  if (text === "a hundred" || text === "one hundred") {
    return 100;
  }
  const [tens, unit] = text.split(RE_HYPHEN_OR_SPACE);
  if (tens in TENS_WORDS) {
    return TENS_WORDS[tens] + (unit ? (UNIT_WORDS[unit] ?? 0) : 0);
  }
  return UNIT_WORDS[text] ?? null;
}

/* -------------------------------------------------------------- directives */

export const DIRECTIVE_IDS = [
  "to-what-extent",
  "how-far",
  "agree",
  "compare-contrast",
  "compare",
  "contrast",
  "analyze",
  "evaluate",
  "assess",
  "discuss",
  "argue",
  "explain",
  "describe",
  "critique",
  "critically",
  "synthesize",
  "justify",
  "examine",
  "outline",
  "summarize",
  "illustrate",
  "define",
  "identify",
  "interpret",
  "explore",
  "review",
  "reflect",
  "propose",
  "recommend",
  "defend",
  "demonstrate",
  "show",
  "trace",
  "classify",
  "distinguish",
  "relate",
  "account-for",
  "comment-on",
  "consider",
  "state",
  "list",
  "apply",
  "predict",
  "respond",
  "choose",
  "develop",
  "persuade",
  "investigate",
  "elaborate",
  "annotate",
  "narrate",
  "prove",
  "formulate",
  "why",
  "how",
] as const;

export type DirectiveId = (typeof DIRECTIVE_IDS)[number];

interface DirectiveRule {
  id: DirectiveId;
  pattern: RegExp;
  /** Capture group holding the word, when the pattern also matches a prefix. */
  group?: number;
}

const DIRECTIVE_RULES: readonly DirectiveRule[] = [
  { id: "to-what-extent", pattern: /\bto what (?:extent|degree)\b/gi },
  { id: "how-far", pattern: /\bhow far\b/gi },
  { id: "agree", pattern: /\b(?:do you agree|agree or disagree)\b/gi },
  { id: "compare-contrast", pattern: /\bcompare\s+and\s+contrast\b/gi },
  { id: "compare", pattern: /\bcompar(?:e|es|ing|isons?)\b/gi },
  { id: "contrast", pattern: /\bcontrast(?:s|ing)?\b/gi },
  { id: "analyze", pattern: /\banaly[sz](?:e|es|ing)\b|\banalys[ie]s\b/gi },
  { id: "evaluate", pattern: /\bevaluat(?:e|es|ing)\b/gi },
  { id: "assess", pattern: /\bassess(?:es|ing)?\b/gi },
  { id: "discuss", pattern: /\bdiscuss(?:es|ing)?\b/gi },
  {
    id: "argue",
    pattern:
      /\bargu(?:e|es|ing)\b|\bargumentative\b|\b(?:an|your|make|construct|build|present)\s+argument\b/gi,
  },
  { id: "explain", pattern: /\bexplain(?:s|ing)?\b/gi },
  { id: "describe", pattern: /\bdescrib(?:e|es|ing)\b/gi },
  { id: "critique", pattern: /\bcritiqu(?:e|es|ing)\b/gi },
  { id: "critically", pattern: /\bcritically\b/gi },
  {
    id: "synthesize",
    pattern: /\bsynthesi[sz](?:e|es|ing)\b|\bsynthesis\b/gi,
  },
  { id: "justify", pattern: /\bjustif(?:y|ies|ying|ication)\b/gi },
  { id: "examine", pattern: /\bexamin(?:e|es|ing)\b/gi },
  { id: "outline", pattern: /\boutlin(?:e|es|ing)\b/gi },
  { id: "summarize", pattern: /\bsummari[sz](?:e|es|ing)\b/gi },
  { id: "illustrate", pattern: /\billustrat(?:e|es|ing)\b/gi },
  { id: "define", pattern: /\bdefin(?:e|es|ing)\b/gi },
  { id: "identify", pattern: /\bidentif(?:y|ies|ying)\b/gi },
  { id: "interpret", pattern: /\binterpret(?:s|ing)?\b/gi },
  { id: "explore", pattern: /\bexplor(?:e|es|ing)\b/gi },
  { id: "review", pattern: /\breview(?:s|ing)?\b/gi },
  { id: "reflect", pattern: /\breflect(?:s|ing|ions?|ive)?\b/gi },
  { id: "propose", pattern: /\bpropos(?:e|es|ing|als?)\b/gi },
  { id: "recommend", pattern: /\brecommend(?:s|ing|ations?)?\b/gi },
  { id: "defend", pattern: /\bdefend(?:s|ing)?\b/gi },
  { id: "demonstrate", pattern: /\bdemonstrat(?:e|es|ing)\b/gi },
  { id: "show", pattern: /\bshow (?:how|that|why|whether)\b/gi },
  { id: "trace", pattern: /\btrac(?:e|es|ing)\b/gi },
  {
    id: "classify",
    pattern: /\bclassif(?:y|ies|ying)\b|\bcategori[sz](?:e|es|ing)\b/gi,
  },
  {
    id: "distinguish",
    pattern: /\bdistinguish(?:es|ing)?\b|\bdifferentiat(?:e|es|ing)\b/gi,
  },
  { id: "relate", pattern: /\brelat(?:e|es)\b/gi },
  { id: "account-for", pattern: /\baccount(?:s|ing)? for\b/gi },
  { id: "comment-on", pattern: /\bcomment(?:s|ing)? on\b/gi },
  { id: "consider", pattern: /\bconsider(?:s|ing)?\b/gi },
  {
    id: "state",
    pattern:
      /\bstate\b(?=\s+(?:your|the|a|an|whether|how|why|what|which|clearly|briefly)\b)/gi,
  },
  {
    id: "list",
    pattern:
      /\blist\b(?=\s+(?:the|your|all|some|any|each|at\s+least|two|three|four|five|six|seven|eight|nine|ten|\d+)\b)/gi,
  },
  {
    id: "apply",
    pattern:
      /\bappl(?:y|ying)\b(?=\s+(?:the|a|an|this|these|that|those|your|one|two|three|it|them|concepts?|theor(?:y|ies)|ideas?|principles?|models?|frameworks?)\b)/gi,
  },
  { id: "predict", pattern: /\bpredict(?:s|ing|ions?)?\b/gi },
  { id: "respond", pattern: /\brespond(?:s|ing)? to\b|\bresponse to\b/gi },
  {
    id: "choose",
    pattern:
      /\b(?:choose|select)\b(?=\s+(?:one|two|three|a|an|any|from|between|among|your)\b)/gi,
  },
  { id: "develop", pattern: /\bdevelop\b(?=\s+(?:a|an|your|the)\b)/gi },
  {
    id: "persuade",
    pattern: /\bpersua(?:de|des|ding|sive)\b|\bconvinc(?:e|es|ing)\b/gi,
  },
  { id: "investigate", pattern: /\binvestigat(?:e|es|ing|ions?)\b/gi },
  { id: "elaborate", pattern: /\belaborat(?:e|es|ing)\b/gi },
  { id: "annotate", pattern: /\bannotat(?:e|es|ed|ing|ions?)\b/gi },
  { id: "narrate", pattern: /\bnarrat(?:e|es|ing|ive)\b/gi },
  { id: "prove", pattern: /\bprove\b(?=\s+(?:that|how|why|whether)\b)/gi },
  { id: "formulate", pattern: /\bformulat(?:e|es|ing)\b/gi },
  { id: "why", pattern: /(^|[.?!:]\s+|\n\s*)(why)\b/gi, group: 2 },
  {
    id: "how",
    pattern:
      /(^|[.?!:]\s+|\n\s*)(how)\b(?!\s+(?:far|many|much|long|often)\b)/gi,
    group: 2,
  },
];

/** "the state of…", "a welfare state" — the noun, not the instruction. */
const RE_STATE_NOUN_BEFORE =
  /\b(?:the|a|this|that|each|every|nation|member|police|welfare|united|steady|solid|liquid|gaseous|current)\s+$/i;

export interface DirectiveHit {
  id: DirectiveId;
  /** Every place the word appears, in order. */
  spans: Span[];
  /** The first occurrence as written in the prompt. */
  text: string;
}

interface RawHit extends Span {
  id: DirectiveId;
}

function rawDirectiveHits(text: string): RawHit[] {
  const hits: RawHit[] = [];
  for (const rule of DIRECTIVE_RULES) {
    for (const match of text.matchAll(rule.pattern)) {
      const word = rule.group ? match[rule.group] : match[0];
      const start =
        rule.group && match[1] !== undefined
          ? match.index + match[1].length
          : match.index;
      if (
        rule.id === "state" &&
        RE_STATE_NOUN_BEFORE.test(text.slice(Math.max(0, start - 20), start))
      ) {
        continue;
      }
      hits.push({ id: rule.id, start, end: start + word.length });
    }
  }
  return hits;
}

/** Keep the longest match where two overlap: "compare and contrast" beats "compare". */
function resolveOverlaps<T extends Span>(hits: readonly T[]): T[] {
  const ordered = [...hits].sort(
    (a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start
  );
  const kept: T[] = [];
  for (const hit of ordered) {
    if (!kept.some((other) => hit.start < other.end && other.start < hit.end)) {
      kept.push(hit);
    }
  }
  return kept.sort((a, b) => a.start - b.start);
}

export function findDirectives(text: string): DirectiveHit[] {
  const byId = new Map<DirectiveId, DirectiveHit>();
  for (const hit of resolveOverlaps(rawDirectiveHits(text))) {
    const span = { start: hit.start, end: hit.end };
    const existing = byId.get(hit.id);
    if (existing) {
      existing.spans.push(span);
    } else {
      byId.set(hit.id, {
        id: hit.id,
        spans: [span],
        text: text.slice(hit.start, hit.end),
      });
    }
  }
  return [...byId.values()];
}

/* ------------------------------------------------------------------ length */

export type LengthUnit =
  | "words"
  | "pages"
  | "paragraphs"
  | "characters"
  | "minutes";

export interface LengthRequirement extends Span {
  unit: LengthUnit;
  min: number | null;
  max: number | null;
  /** "about 1,500 words", or a bare "a 1,500-word essay": a target, not a limit. */
  approximate: boolean;
  text: string;
}

const RE_LENGTH = new RegExp(
  `\\b(?:between\\s+(${NUM})\\s+(?:and|to)\\s+(${NUM})|(${NUM})\\s*(?:-|–|—|to)\\s*(${NUM})|(${NUM}))(\\+)?(?:\\s+|-)(?:(?:full|double[-\\s]spaced|single[-\\s]spaced|typed|typewritten|written|complete)\\s+){0,2}(words?|pages?|paragraphs?|characters?|minutes?)\\b`,
  "gi"
);
const RE_MIN_BEFORE =
  /(?:at\s+least|a\s+minimum\s+of|minimum\s+of|minimum|no\s+(?:fewer|less)\s+than|not\s+(?:fewer|less)\s+than|min\.?)\s*$/i;
const RE_MAX_BEFORE =
  /(?:no\s+more\s+than|not\s+more\s+than|a\s+maximum\s+of|maximum\s+of|maximum|up\s+to|at\s+most|no\s+longer\s+than|not\s+(?:to\s+)?exceed(?:ing)?|max\.?|under|fewer\s+than|less\s+than)\s*$/i;
const RE_APPROX_BEFORE =
  /(?:approximately|about|around|roughly|circa|~|approx\.?)\s*$/i;
const RE_MIN_AFTER = /^\s*\(?\s*(?:minimum|min\.?|or\s+more|at\s+minimum)\b/i;
const RE_MAX_AFTER =
  /^\s*\(?\s*(?:maximum|max\.?|or\s+(?:fewer|less)|at\s+most|limit)\b/i;
const RE_TOLERANCE_AFTER = /^\s*\(?\s*(?:±|\+\/-|plus\s+or\s+minus)/i;
/**
 * A reading assignment in the same clause — "Read pages 45–60 and 20 pages of…" — unless
 * a writing verb follows it: "Read the article and write 1,500 words" is a length.
 */
const RE_READING_CLAUSE =
  /\bread(?:ing|s)?\b(?![^.;:,!?\n]*\b(?:write|writing|compose|produce|draft|submit|respond)\b)[^.;:,!?\n]*$/i;
/** "20 pages of the textbook" — pages to read, not to write. */
const RE_READING_AFTER =
  /^\s+(?:of|from|in)\s+(?:the\s+|your\s+)?(?:textbook|book|reading|readings|chapter|article|novel|text|play|packet)\b/i;

const UNIT_OF: Record<string, LengthUnit> = {
  word: "words",
  page: "pages",
  paragraph: "paragraphs",
  character: "characters",
  minute: "minutes",
};

const RE_PLURAL_S = /s$/;

function lengthUnit(raw: string): LengthUnit {
  return UNIT_OF[raw.toLowerCase().replace(RE_PLURAL_S, "")] ?? "words";
}

type Bound = "min" | "max" | "approx" | null;

function boundBefore(before: string): { bound: Bound; length: number } {
  for (const [bound, pattern] of [
    ["min", RE_MIN_BEFORE],
    ["max", RE_MAX_BEFORE],
    ["approx", RE_APPROX_BEFORE],
  ] as const) {
    const match = pattern.exec(before);
    if (match) {
      return { bound, length: match[0].length };
    }
  }
  return { bound: null, length: 0 };
}

function boundAfter(after: string): Bound {
  if (RE_MIN_AFTER.test(after)) {
    return "min";
  }
  if (RE_MAX_AFTER.test(after)) {
    return "max";
  }
  return RE_TOLERANCE_AFTER.test(after) ? "approx" : null;
}

function lengthFrom(
  match: RegExpMatchArray,
  text: string
): LengthRequirement | null {
  const start = match.index ?? 0;
  const end = start + match[0].length;
  const before = text.slice(Math.max(0, start - 30), start);
  if (
    RE_READING_CLAUSE.test(text.slice(Math.max(0, start - 60), start)) ||
    RE_READING_AFTER.test(text.slice(end, end + 40))
  ) {
    return null;
  }
  const low = parseCount(match[1] ?? match[3] ?? "");
  const high = parseCount(match[2] ?? match[4] ?? "");
  const single = parseCount(match[5] ?? "");
  const unit = lengthUnit(match[7]);
  const prefix = boundBefore(before);
  const bound =
    match[6] === "+" ? "min" : (prefix.bound ?? boundAfter(text.slice(end)));
  const from = start - prefix.length;
  const base = { unit, start: from, end, text: text.slice(from, end) };
  if (low !== null && high !== null) {
    return { ...base, min: low, max: high, approximate: false };
  }
  if (single === null) {
    return null;
  }
  return {
    ...base,
    min: bound === "max" ? null : single,
    max: bound === "min" ? null : single,
    approximate: bound === "approx" || bound === null,
  };
}

export function findLengths(text: string): LengthRequirement[] {
  const found: LengthRequirement[] = [];
  for (const match of text.matchAll(RE_LENGTH)) {
    const requirement = lengthFrom(match, text);
    if (requirement) {
      found.push(requirement);
    }
  }
  return found;
}

/* ----------------------------------------------------------------- sources */

const SOURCE_NOUN =
  "(?:sources?|references?|citations?|articles?|studies|study|texts?|documents?|books?|works|readings?|journals?|websites?|papers?|quotations?)";
const SOURCE_ADJECTIVE =
  "(?:scholarly|peer[-\\s]reviewed|academic|primary|secondary|credible|reliable|reputable|outside|external|independent|published|recent|provided|assigned|course|empirical|quantitative|qualitative|print|online|different|additional|relevant|authoritative|current|historical|original|research|journal|nonfiction|non-fiction|of\\s+the|of\\s+these)";

const RE_SOURCE_COUNT = new RegExp(
  `\\b(${NUM})(\\+|\\s+or\\s+more)?(?:\\s*(?:-|–|—|to|or)\\s*(${NUM}))?\\s+(?:${SOURCE_ADJECTIVE}[\\s,]+(?:and\\s+|or\\s+)?){0,4}${SOURCE_NOUN}\\b`,
  "gi"
);
const RE_SOURCE_TYPE =
  /\b(?:peer[-\s]reviewed|scholarly|primary\s+sources?|secondary\s+sources?|academic\s+(?:journals?|sources?)|credible\s+sources?|reputable\s+sources?)\b/gi;
const RE_SOURCE_BAN =
  /\b(?:no|not|avoid|do\s+not\s+use|don['’]t\s+use|cannot\s+use|can['’]t\s+use|may\s+not\s+use|should\s+not\s+use|without(?:\s+using)?)\s+(?:(?:any|the|a)\s+)?(?:wikipedia|websites?|blogs?|encyclopedias?|dictionaries|\.com\s+sites?|online\s+sources?|ai\s+tools?|chatgpt|generative\s+ai|social\s+media)\b/gi;
const RE_SOURCE_RECENCY = new RegExp(
  `\\b(?:published\\s+)?(?:within|in|from)\\s+the\\s+(?:last|past)\\s+(?:${NUM})\\s+years?\\b|\\b(?:published\\s+)?(?:since|after)\\s+(?:19|20)\\d{2}\\b`,
  "gi"
);

export type SourceKind = "count" | "type" | "ban" | "recency";

export interface SourceRequirement extends Span {
  kind: SourceKind;
  text: string;
  min: number | null;
  max: number | null;
}

function sourceCount(
  match: RegExpMatchArray,
  text: string
): SourceRequirement | null {
  const start = match.index ?? 0;
  const end = start + match[0].length;
  const first = parseCount(match[1]);
  if (first === null) {
    return null;
  }
  const second = match[3] ? parseCount(match[3]) : null;
  const before = text.slice(Math.max(0, start - 30), start);
  const prefix = boundBefore(before);
  const after = prefix.bound === null ? boundAfter(text.slice(end)) : null;
  const atLeast =
    Boolean(match[2]) || prefix.bound === "min" || after === "min";
  const from = start - prefix.length;
  const exactOrRange = second ?? (atLeast ? null : first);
  return {
    kind: "count",
    start: from,
    end,
    text: text.slice(from, end),
    min: prefix.bound === "max" || after === "max" ? null : first,
    max: prefix.bound === "max" || after === "max" ? first : exactOrRange,
  };
}

function simpleMatches(
  text: string,
  pattern: RegExp,
  kind: SourceKind
): SourceRequirement[] {
  return [...text.matchAll(pattern)].map((match) => ({
    kind,
    start: match.index,
    end: match.index + match[0].length,
    text: match[0],
    min: null,
    max: null,
  }));
}

export function findSources(text: string): SourceRequirement[] {
  const counts = [...text.matchAll(RE_SOURCE_COUNT)]
    .map((match) => sourceCount(match, text))
    .filter((item): item is SourceRequirement => item !== null);
  const others = [
    ...simpleMatches(text, RE_SOURCE_BAN, "ban"),
    ...simpleMatches(text, RE_SOURCE_RECENCY, "recency"),
    ...simpleMatches(text, RE_SOURCE_TYPE, "type"),
  ];
  return resolveOverlaps([...counts, ...others]);
}

/* ------------------------------------------------------------------ styles */

export interface StyleMention extends Span {
  id: string;
  name: string;
  edition: number | null;
  /** The site's guide to the style. */
  href: string;
}

interface StyleRule {
  id: string;
  name: string;
  href: string;
  pattern: RegExp;
}

const STYLE_CONTEXT =
  "(?=[\\s-]+(?:style|format|formatting|citations?|referenc(?:e|es|ing)|guidelines|manual|\\(?\\d))";

const STYLE_RULES: readonly StyleRule[] = [
  { id: "apa", name: "APA", href: "/apa", pattern: /\bAPA\b/g },
  { id: "mla", name: "MLA", href: "/mla", pattern: /\bMLA\b/g },
  {
    id: "chicago",
    name: "Chicago",
    href: "/chicago",
    pattern:
      /\b(?:Chicago(?=[\s-]+(?:Manual|style|format|citations?|referenc(?:e|es|ing)|notes?|author[-\s]date|\(?\d))|CMOS)\b|\bChicago\b(?=\s*\((?:notes|author))/gi,
  },
  {
    id: "turabian",
    name: "Turabian",
    href: "/turabian",
    pattern: /\bTurabian\b/gi,
  },
  {
    id: "harvard",
    name: "Harvard",
    href: "/harvard",
    pattern:
      /\bHarvard(?=[\s-]+(?:style|referencing|format|system|citations?))/gi,
  },
  { id: "ieee", name: "IEEE", href: "/ieee", pattern: /\bIEEE\b/g },
  {
    id: "vancouver",
    name: "Vancouver",
    href: "/vancouver",
    pattern: /\bVancouver(?=[\s-]+(?:style|referencing|format|system))/gi,
  },
  {
    id: "ama",
    name: "AMA",
    href: "/ama",
    pattern: new RegExp(`\\bAMA\\b${STYLE_CONTEXT}`, "g"),
  },
  {
    id: "asa",
    name: "ASA",
    href: "/asa",
    pattern: new RegExp(`\\bASA\\b${STYLE_CONTEXT}`, "g"),
  },
  { id: "apsa", name: "APSA", href: "/apsa", pattern: /\bAPSA\b/g },
  {
    id: "cse",
    name: "CSE",
    href: "/cse",
    pattern: new RegExp(`\\bCSE\\b${STYLE_CONTEXT}`, "g"),
  },
  {
    id: "acs",
    name: "ACS",
    href: "/acs",
    pattern: new RegExp(`\\bACS\\b${STYLE_CONTEXT}`, "g"),
  },
  { id: "oscola", name: "OSCOLA", href: "/oscola", pattern: /\bOSCOLA\b/g },
  {
    id: "bluebook",
    name: "Bluebook",
    href: "/bluebook",
    pattern: /\bBluebook\b/gi,
  },
  { id: "aglc", name: "AGLC", href: "/aglc", pattern: /\bAGLC\b/g },
];

/**
 * An edition straight after the style's name: "7th edition", "(7th ed.)", "17th", or a
 * bare "APA 7" — but not "APA, 5 sources", where the number counts something else.
 */
const RE_EDITION_AFTER =
  /^(?:\s+Manual\s+of\s+Style)?(?:,?\s*\(?\s*(\d{1,2})(?:st|nd|rd|th)(?:\s*ed(?:ition)?\.?)?\)?|\s*\(?(\d{1,2})\s*ed(?:ition)?\.?\)?|\s(\d{1,2})(?=[\s,.;:)]|$)(?!\s+(?:sources?|references?|pages?|words?|citations?|articles?|studies|paragraphs?)\b))/i;

export function findStyles(text: string): StyleMention[] {
  const found: StyleMention[] = [];
  for (const rule of STYLE_RULES) {
    for (const match of text.matchAll(rule.pattern)) {
      const start = match.index;
      const nameEnd = start + match[0].length;
      const edition = RE_EDITION_AFTER.exec(text.slice(nameEnd));
      const end = edition ? nameEnd + edition[0].length : nameEnd;
      found.push({
        id: rule.id,
        name: rule.name,
        href: rule.href,
        start,
        end,
        edition: edition
          ? Number(edition[1] ?? edition[2] ?? edition[3])
          : null,
      });
    }
  }
  return found.sort((a, b) => a.start - b.start);
}

/** One entry per style, keeping the first mention that names an edition. */
export function distinctStyles(
  styles: readonly StyleMention[]
): StyleMention[] {
  const byId = new Map<string, StyleMention>();
  for (const style of styles) {
    const existing = byId.get(style.id);
    if (!existing || (existing.edition === null && style.edition !== null)) {
      byId.set(style.id, style);
    }
  }
  return [...byId.values()].sort((a, b) => a.start - b.start);
}

/* ------------------------------------------------------------------- dates */

const MONTH =
  "(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan\\.?|Feb\\.?|Mar\\.?|Apr\\.?|Jun\\.?|Jul\\.?|Aug\\.?|Sept?\\.?|Oct\\.?|Nov\\.?|Dec\\.?)";
/** Full names take no period — "Monday." ends a sentence; "Mon." is an abbreviation. */
const WEEKDAY =
  "(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Mon\\.?|Tues?\\.?|Wed\\.?|Thu(?:rs?)?\\.?|Fri\\.?|Sat\\.?|Sun\\.?)";
const DAY = "\\d{1,2}(?:st|nd|rd|th)?";
const TIME = "\\d{1,2}(?::\\d{2})?\\s*(?:a\\.?m\\.?|p\\.?m\\.?)|midnight|noon";

const RE_DATE = new RegExp(
  `\\b(?:(?:${WEEKDAY},?\\s+)?${MONTH}\\s+${DAY}(?:,?\\s+\\d{4})?|(?:${WEEKDAY},?\\s+)?${DAY}\\s+(?:of\\s+)?${MONTH}(?:,?\\s+\\d{4})?|\\d{4}-\\d{2}-\\d{2}|\\d{1,2}/\\d{1,2}(?:/\\d{2,4})?)(?![\\d/])`,
  "gi"
);
const RE_WEEKDAY_DEADLINE = new RegExp(
  `\\b(?:due|by|before|until|deadline:?)\\s+(?:(?:this|next)\\s+)?(${WEEKDAY})(?![\\w,]*\\s+${DAY})`,
  "gi"
);
const RE_TIME_BEFORE = new RegExp(`(${TIME})\\s*(?:on|,)?\\s*$`, "i");
const RE_TIME_AFTER = new RegExp(
  `^\\s*(?:,|at|by|before)?\\s*(${TIME})(?:\\s+[A-Z]{2,4}\\b)?`,
  "i"
);
const RE_DEADLINE_WORD =
  /\b(?:due|deadline|submit(?:ted)?|submission|turn(?:ed)?\s+in|hand(?:ed)?\s+in|upload(?:ed)?|no\s+later\s+than|by)\b/i;
const RE_SLASH_DATE = /^\d{1,2}\/\d{1,2}$/;
const RE_SENTENCE_BREAK = /[.?!]\s+(?=[A-Z])|\n/g;

export interface DateMention extends Span {
  text: string;
  time: string | null;
  deadline: boolean;
  /** "draft" or "final" when the sentence says which submission the date is for. */
  stage: "draft" | "final" | null;
}

const RE_DRAFT = /\bdrafts?\b/i;
const RE_FINAL = /\bfinal\b/i;

function stageOf(before: string): DateMention["stage"] {
  const lastDraft = before.search(RE_DRAFT);
  const lastFinal = before.search(RE_FINAL);
  if (lastFinal === -1 && lastDraft === -1) {
    return null;
  }
  return lastFinal > lastDraft ? "final" : "draft";
}

/** The part of the text before `index` that belongs to the same sentence. */
function sentenceBefore(text: string, index: number, limit = 80): string {
  const window = text.slice(Math.max(0, index - limit), index);
  let cut = 0;
  for (const match of window.matchAll(RE_SENTENCE_BREAK)) {
    cut = match.index + match[0].length;
  }
  return window.slice(cut);
}

function dateMention(
  text: string,
  start: number,
  end: number
): DateMention | null {
  const date = text.slice(start, end);
  const before = sentenceBefore(text, start);
  const deadline = RE_DEADLINE_WORD.test(before);
  if (RE_SLASH_DATE.test(date) && !deadline) {
    return null;
  }
  const timeBefore = RE_TIME_BEFORE.exec(before);
  const timeAfter = RE_TIME_AFTER.exec(text.slice(end, end + 30));
  const clause = before.split(";").at(-1) ?? before;
  return {
    start,
    end,
    text: date,
    time: timeBefore?.[1] ?? timeAfter?.[1] ?? null,
    deadline,
    stage: deadline ? stageOf(clause) : null,
  };
}

export function findDates(text: string): DateMention[] {
  const found: DateMention[] = [];
  for (const match of text.matchAll(RE_DATE)) {
    const mention = dateMention(
      text,
      match.index,
      match.index + match[0].length
    );
    if (mention) {
      found.push(mention);
    }
  }
  for (const match of text.matchAll(RE_WEEKDAY_DEADLINE)) {
    const start = match.index + match[0].length - match[1].length;
    const mention = dateMention(text, start, start + match[1].length);
    if (mention) {
      found.push(mention);
    }
  }
  return resolveOverlaps(found);
}

/* -------------------------------------------------------------- formatting */

export const FORMAT_IDS = [
  "doubleSpaced",
  "singleSpaced",
  "spacing15",
  "fontSize",
  "font",
  "margins",
  "titlePage",
  "runningHead",
  "pageNumbers",
  "headings",
  "mlaHeading",
  "abstract",
  "referencesPage",
  "footnotes",
  "inText",
  "fileFormat",
  "submission",
  "tableOfContents",
  "appendix",
  "wordCountStatement",
] as const;

export type FormatId = (typeof FORMAT_IDS)[number];

const FORMAT_RULES: readonly { id: FormatId; pattern: RegExp }[] = [
  { id: "doubleSpaced", pattern: /\bdouble[-\s]?spac(?:ed|ing)\b/gi },
  { id: "singleSpaced", pattern: /\bsingle[-\s]?spac(?:ed|ing)\b/gi },
  {
    id: "spacing15",
    pattern: /\b1\.5[-\s]?(?:line\s+)?spac(?:ed|ing)\b/gi,
  },
  {
    id: "fontSize",
    pattern:
      /\b(?:1[0-4]|[89])[-\s]?(?:pt|point)\b(?:\s+font)?|\bfont\s+size\s+(?:of\s+)?(?:1[0-4]|[89])\b/gi,
  },
  {
    id: "font",
    pattern:
      /\b(?:Times\s+New\s+Roman|Arial|Calibri|Garamond|Georgia|Helvetica|Cambria|Verdana)\b/g,
  },
  {
    id: "margins",
    pattern:
      /\b(?:\d(?:\.\d+)?|one)[-\s]?(?:inch|in\.?|cm)\s+margins?\b|\bmargins?\s+of\s+(?:\d(?:\.\d+)?|one)[-\s]?(?:inch(?:es)?|in\.?|cm)\b/gi,
  },
  { id: "titlePage", pattern: /\b(?:title|cover)\s+page\b/gi },
  { id: "runningHead", pattern: /\brunning\s+head(?:er)?\b/gi },
  {
    id: "pageNumbers",
    pattern:
      /\bpage\s+numbers?\b|\bnumber(?:ed)?\s+(?:the\s+|your\s+|all\s+)?pages\b/gi,
  },
  { id: "headings", pattern: /\b(?:sub)?headings\b/gi },
  { id: "mlaHeading", pattern: /\bMLA\s+heading\b/gi },
  {
    id: "abstract",
    pattern:
      /\b(?:an?|the|include\s+an?|with\s+an?)\s+abstract\b(?!\s+(?:concepts?|ideas?|art|thinking|nouns?|reasoning))/gi,
  },
  {
    id: "referencesPage",
    pattern:
      /\bworks\s+cited\b|\breferences?\s+(?:page|list|section)\b|\bbibliography\b/gi,
  },
  { id: "footnotes", pattern: /\bfoot\s?notes?\b|\bend\s?notes?\b/gi },
  {
    id: "inText",
    pattern: /\bin[-\s]text\s+citations?\b|\bparenthetical\s+citations?\b/gi,
  },
  {
    id: "fileFormat",
    pattern:
      /\bPDF\b|\.pdf\b|\.docx?\b|\bWord\s+(?:document|file)\b|\bMicrosoft\s+Word\b|\bGoogle\s+Docs?\b/gi,
  },
  {
    id: "submission",
    pattern:
      /\b(?:Canvas|Blackboard|Moodle|Turnitin|Google\s+Classroom|D2L|Brightspace|Schoology)\b/g,
  },
  { id: "tableOfContents", pattern: /\btable\s+of\s+contents\b/gi },
  { id: "appendix", pattern: /\bappendi(?:x|ces)\b/gi },
  {
    id: "wordCountStatement",
    pattern: /\b(?:include|state|list)\s+(?:a|the|your)\s+word\s+count\b/gi,
  },
];

export interface FormatMention extends Span {
  id: FormatId;
  text: string;
}

export function findFormatting(text: string): FormatMention[] {
  const found: FormatMention[] = [];
  for (const rule of FORMAT_RULES) {
    for (const match of text.matchAll(rule.pattern)) {
      found.push({
        id: rule.id,
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
      });
    }
  }
  return resolveOverlaps(found);
}

/** One mention per formatting rule, in prompt order. */
export function distinctFormatting(
  items: readonly FormatMention[]
): FormatMention[] {
  const seen = new Set<FormatId>();
  return items.filter((item) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}

/* --------------------------------------------------------------- key terms */

const TERM_STOP_WORDS: ReadonlySet<string> = new Set(
  (
    "a about above across after again against all almost also although always am among an " +
    "and another any anything are around as at be because been before being below between " +
    "both but by can cannot could did do does doing done down during each either else end " +
    "ends ended enough even ever every few for from further had has have having he her here " +
    "hers him his how however i if in into is it its itself just least less like made make " +
    "makes many may me might more most much must my neither no nor not now of off often on " +
    "once one only or other others our out over own per rather same she should since so some " +
    "such than that the their them then there these they this those though through thus to " +
    "too toward towards under until up upon us use used uses using very via was we well were " +
    "what when where whether which while who whom whose why will with within without would " +
    "yet you your yours first second third fourth fifth last next least also etc e.g i.e " +
    "essay essays paper papers assignment assignments prompt prompts question questions " +
    "response responses answer answers student students class course instructor teacher " +
    "professor write writing written words word pages page paragraph paragraphs sources source " +
    "include including included cite citing cited citation citations format formatted style " +
    "submit submitted submission due length minimum maximum double spaced font point margins " +
    "text texts following follow example examples topic topics please note sure make clearly " +
    "clear specific specifically discuss discussion section sections part parts total points " +
    "grade graded grading rubric draft final reading readings provide provided support " +
    "supporting evidence claim claims thesis position view views way ways thing things " +
    "extent degree far relevant appropriate least two three four five six seven eight nine ten " +
    "notes bibliography references reference works cited style styles edition read start " +
    "begin worth referencing formatting discussed evaluated assessed graded described explained analyzed analysed covered"
  ).split(" ")
);

const RE_TERM_TOKEN = /[\p{L}][\p{L}'’-]*/gu;
const RE_QUOTED_TERM = /[“"]([^”"\n]{2,60})[”"]/g;
const RE_TERM_SPLIT = /[.,;:!?()[\]{}“”"/–—]|\s-\s/;
const RE_STARTS_UPPER = /^\p{Lu}/u;
const RE_POSSESSIVE_END = /['’]s$/;

export interface KeyTerm {
  text: string;
  count: number;
}

/** Text with every span blanked to spaces, so positions survive. */
function maskSpans(text: string, spans: readonly Span[]): string {
  // Code units, not code points: the spans are UTF-16 offsets.
  const chars = text.split("");
  for (const span of spans) {
    for (let index = span.start; index < span.end; index++) {
      chars[index] = " ";
    }
  }
  return chars.join("");
}

function isTermWord(word: string): boolean {
  const bare = word.replace(RE_POSSESSIVE_END, "");
  return bare.length >= 3 && !TERM_STOP_WORDS.has(bare.toLowerCase());
}

/** Short joining words that may sit inside a name or title: "Declaration of Independence". */
const NAME_JOINERS: ReadonlySet<string> = new Set([
  "of",
  "the",
  "and",
  "a",
  "an",
  "in",
  "on",
  "to",
  "for",
  "de",
  "van",
  "von",
]);

type RunKind = "upper" | "lower";

interface Run {
  kind: RunKind;
  words: string[];
}

/** Drop function words from either end of a run. */
function trimRun(words: readonly string[]): string[] {
  let first = 0;
  let last = words.length;
  while (first < last && !isTermWord(words[first])) {
    first += 1;
  }
  while (last > first && !isTermWord(words[last - 1])) {
    last -= 1;
  }
  return words.slice(first, last);
}

/** A capitalised run may be a name or title of up to six words; a lowercase one, three. */
function runPhrases(run: Run): string[] {
  const words = trimRun(run.words);
  if (words.length === 0) {
    return [];
  }
  const limit = run.kind === "upper" ? 6 : 3;
  return words.length <= limit ? [words.join(" ")] : words.filter(isTermWord);
}

/** Does a function word continue the capitalised run it sits in? */
function joinsName(word: string, next: string | undefined, run: Run | null) {
  const capitalNext = Boolean(
    next && RE_STARTS_UPPER.test(next) && isTermWord(next)
  );
  if (RE_STARTS_UPPER.test(word)) {
    return capitalNext || run?.kind === "upper";
  }
  return run?.kind === "upper" && NAME_JOINERS.has(word) && capitalNext;
}

/**
 * Runs of content words, split wherever the case changes — "did the Roman Republic
 * collapse" gives "Roman Republic" and "collapse" — with names and titles kept whole.
 */
function candidatePhrases(masked: string): string[] {
  const phrases: string[] = [];
  for (const chunk of masked.split(RE_TERM_SPLIT)) {
    const tokens = [...chunk.matchAll(RE_TERM_TOKEN)].map((match) =>
      match[0].replace(RE_POSSESSIVE_END, "")
    );
    let run: Run | null = null;
    const flush = () => {
      if (run) {
        phrases.push(...runPhrases(run));
      }
      run = null;
    };
    for (const [index, word] of tokens.entries()) {
      const kind: RunKind = RE_STARTS_UPPER.test(word) ? "upper" : "lower";
      if (!isTermWord(word)) {
        if (joinsName(word, tokens[index + 1], run)) {
          run = run ?? { kind: "upper", words: [] };
          run.words.push(word);
        } else {
          flush();
        }
        continue;
      }
      if (run && run.kind !== kind) {
        flush();
      }
      run = run ?? { kind, words: [] };
      run.words.push(word);
    }
    flush();
  }
  return phrases;
}

const RE_LETTER = /\p{L}/u;

/** Whole-word occurrences: "art" is not counted inside "start". */
function countOccurrences(haystack: string, needle: string): number {
  if (needle === "") {
    return 0;
  }
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    const before = haystack[index - 1] ?? " ";
    const after = haystack[index + needle.length] ?? " ";
    if (!(RE_LETTER.test(before) || RE_LETTER.test(after))) {
      count += 1;
    }
    index = haystack.indexOf(needle, index + 1);
  }
  return count;
}

/** The prompt's content words and phrases, most prominent first. */
export function findKeyTerms(
  text: string,
  masked: readonly Span[],
  limit = 10
): KeyTerm[] {
  const lower = text.toLowerCase();
  const scored = new Map<
    string,
    { text: string; score: number; first: number; count: number }
  >();
  const consider = (phrase: string) => {
    const key = phrase.toLowerCase();
    if (scored.has(key)) {
      return;
    }
    const count = Math.max(1, countOccurrences(lower, key));
    const words = phrase.split(" ").length;
    scored.set(key, {
      text: phrase,
      count,
      score: count * (1 + 0.5 * (words - 1)),
      first: lower.indexOf(key),
    });
  };
  for (const match of text.matchAll(RE_QUOTED_TERM)) {
    consider(match[1].trim());
  }
  for (const phrase of candidatePhrases(maskSpans(text, masked))) {
    consider(phrase);
  }
  const ranked = [...scored.values()].sort(
    (a, b) => b.score - a.score || a.first - b.first
  );
  const kept: typeof ranked = [];
  for (const term of ranked) {
    const key = term.text.toLowerCase();
    const inside = kept.some(
      (other) =>
        other.text.toLowerCase().includes(key) && other.count >= term.count
    );
    if (!inside) {
      kept.push(term);
    }
  }
  return kept.slice(0, limit).map(({ text: phrase, count }) => ({
    text: phrase,
    count,
  }));
}

/* ---------------------------------------------------------------- analysis */

export interface PromptAnalysis {
  directives: DirectiveHit[];
  lengths: LengthRequirement[];
  sources: SourceRequirement[];
  styles: StyleMention[];
  dates: DateMention[];
  formatting: FormatMention[];
  terms: KeyTerm[];
}

export function analyzePrompt(text: string): PromptAnalysis {
  const directives = findDirectives(text);
  const lengths = findLengths(text);
  const sources = findSources(text);
  const styles = findStyles(text);
  const dates = findDates(text);
  const formatting = findFormatting(text);
  const spans: Span[] = [
    ...directives.flatMap((hit) => hit.spans),
    ...lengths,
    ...sources,
    ...styles,
    ...dates,
    ...formatting,
  ];
  return {
    directives,
    lengths,
    sources,
    styles,
    dates,
    formatting,
    terms: findKeyTerms(text, spans),
  };
}

export type HighlightKind =
  | "directive"
  | "length"
  | "sources"
  | "style"
  | "date"
  | "format";

export interface PromptHighlight extends Span {
  kind: HighlightKind;
}

/** Every finding as a span to mark in the prompt, longest first where they overlap. */
export function promptHighlights(analysis: PromptAnalysis): PromptHighlight[] {
  const marks: PromptHighlight[] = [
    ...analysis.directives.flatMap((hit) =>
      hit.spans.map((span) => ({ ...span, kind: "directive" as const }))
    ),
    ...analysis.lengths.map((item) => ({ ...item, kind: "length" as const })),
    ...analysis.sources.map((item) => ({ ...item, kind: "sources" as const })),
    ...analysis.styles.map((item) => ({ ...item, kind: "style" as const })),
    ...analysis.dates.map((item) => ({ ...item, kind: "date" as const })),
    ...analysis.formatting.map((item) => ({
      ...item,
      kind: "format" as const,
    })),
  ];
  return resolveOverlaps(marks).map(({ start, end, kind }) => ({
    start,
    end,
    kind,
  }));
}

/* --------------------------------------------------------------- checklist */

export type StepId =
  | "read"
  | "directive"
  | "terms"
  | "sources"
  | "style"
  | "length"
  | "format"
  | "deadline"
  | "thesis"
  | "final";

export interface ChecklistStep {
  id: StepId;
  /** For a directive step, which directive. */
  directive?: DirectiveId;
  /** Text from the prompt the step quotes. */
  detail?: string;
  /** For a deadline, which submission it is for. */
  stage?: "draft" | "final" | null;
}

/** The step-by-step plan, built only from what was found. */
export function buildChecklist(analysis: PromptAnalysis): ChecklistStep[] {
  const steps: ChecklistStep[] = [{ id: "read" }];
  for (const hit of analysis.directives) {
    steps.push({ id: "directive", directive: hit.id });
  }
  if (analysis.terms.length > 0) {
    steps.push({
      id: "terms",
      detail: analysis.terms
        .slice(0, 5)
        .map((term) => term.text)
        .join(", "),
    });
  }
  for (const source of analysis.sources) {
    steps.push({ id: "sources", detail: source.text });
  }
  for (const style of distinctStyles(analysis.styles)) {
    steps.push({ id: "style", detail: style.name });
  }
  for (const length of analysis.lengths) {
    steps.push({ id: "length", detail: length.text });
  }
  const formats = distinctFormatting(analysis.formatting);
  if (formats.length > 0) {
    steps.push({
      id: "format",
      detail: formats.map((item) => item.text).join(", "),
    });
  }
  for (const date of analysis.dates.filter((item) => item.deadline)) {
    steps.push({
      id: "deadline",
      detail: date.time ? `${date.text}, ${date.time}` : date.text,
      stage: date.stage,
    });
  }
  steps.push({ id: "thesis" }, { id: "final" });
  return steps;
}
